import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import PageHero from "@/components/feature/PageHero";
import StoryCard from "@/components/feature/StoryCard";
import FilterSidebar from "@/pages/news/components/FilterSidebar";
import { stories } from "@/mocks/stories";

const ORIGIN = "https://laisih.cn";

export default function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const category = searchParams.get("category") ?? "全部";
  const query = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? "latest";
  const statusParam = searchParams.get("status") ?? "";
  const statuses = statusParam ? statusParam.split(",") : [];

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handleQuery = (value: string) => patchParams({ q: value });
  const handleCategory = (value: string) => patchParams({ category: value === "全部" ? null : value });
  const handleSort = (value: string) => patchParams({ sort: value });
  const handleToggleStatus = (value: string) => {
    const next = statuses.includes(value)
      ? statuses.filter((item) => item !== value)
      : [...statuses, value];
    patchParams({ status: next.join(",") });
  };
  const handleReset = () => setSearchParams(new URLSearchParams(), { replace: true });

  let list = stories.filter((story) => category === "全部" || story.category === category);

  if (query.trim()) {
    const keyword = query.trim();
    list = list.filter(
      (story) =>
        story.title.includes(keyword) ||
        story.summary.includes(keyword) ||
        story.tags.some((tag) => tag.includes(keyword)),
    );
  }

  if (statuses.length) {
    list = list.filter((story) => statuses.includes(story.status));
  }

  list = [...list].sort((a, b) => {
    if (sort === "heat") return b.heat - a.heat;
    if (sort === "nodes") return b.timeline.length - a.timeline.length;
    return b.date.localeCompare(a.date);
  });

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "全部追踪记录",
      url: `${ORIGIN}/news`,
      inLanguage: "zh-CN",
      description:
        "黑瓜爆料全部事件追踪记录，按线索速报、反转现场、当事人回应与瓜田盘点四类归档，支持关键词搜索与状态筛选。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: ORIGIN },
        { "@type": "ListItem", position: 2, name: "全部追踪", item: `${ORIGIN}/news` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "追踪记录列表",
      numberOfItems: list.length,
      itemListElement: list.slice(0, 10).map((story, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: story.title,
        url: `${ORIGIN}/news/${story.id}`,
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="全部追踪记录｜黑瓜爆料 事件持续跟进列表"
        description="浏览黑瓜爆料全部追踪记录，按线索速报、反转现场、当事人回应与瓜田盘点分类归档，可搜索关键词并按热度、时间或节点数量排序，快速找到你关心的那件事。"
        keywords="黑瓜爆料列表,爆料追踪,事件跟进,反转节点,当事人回应,最新进展"
        path="/news"
        jsonLd={jsonLd}
      />
      <PageHero
        kicker="TRACKING ARCHIVE"
        title="全部追踪记录"
        desc="每一条记录都对应一条独立时间线。可用左侧分类、状态与关键词筛选，找到你已经跟了一半的那件事。"
        crumbs={[{ label: "首页", href: "/" }, { label: "全部追踪" }]}
        aside={
          <div className="flex gap-px overflow-hidden rounded-lg border border-background-300 bg-background-300">
            <div className="bg-background-50 px-4 py-3 md:px-5">
              <p className="font-heading text-xl font-bold tabular-nums text-primary-600 md:text-2xl">
                {list.length}
              </p>
              <p className="font-label text-xs text-foreground-500">当前结果</p>
            </div>
            <div className="bg-background-50 px-4 py-3 md:px-5">
              <p className="font-heading text-xl font-bold tabular-nums text-foreground-900 md:text-2xl">
                {stories.length}
              </p>
              <p className="font-label text-xs text-foreground-500">全部记录</p>
            </div>
          </div>
        }
      />

      <section className="bg-background-50 py-8 md:py-12">
        <div className="mx-auto max-w-[1360px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-3">
              <FilterSidebar
                category={category}
                statuses={statuses}
                sort={sort}
                query={query}
                onCategory={handleCategory}
                onToggleStatus={handleToggleStatus}
                onSort={handleSort}
                onQuery={handleQuery}
                onReset={handleReset}
              />
            </div>

            <div className="lg:col-span-9">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
                <p className="font-label text-sm text-foreground-600">
                  共找到 <span className="font-bold text-primary-600">{list.length}</span> 条追踪记录
                </p>
                <div className="flex flex-wrap gap-2">
                  {category !== "全部" ? (
                    <span className="rounded-full bg-primary-100 px-3 py-1 font-label text-xs text-primary-800">
                      分类：{category}
                    </span>
                  ) : null}
                  {statuses.map((status) => (
                    <span
                      key={status}
                      className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-800"
                    >
                      状态：{status}
                    </span>
                  ))}
                  {query ? (
                    <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs text-accent-900">
                      关键词：{query}
                    </span>
                  ) : null}
                </div>
              </div>

              {list.length ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {list.map((story, index) => (
                    <StoryCard key={story.id} story={story} variant="grid" index={index} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                  <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-inbox-archive-line text-2xl" />
                  </span>
                  <h2 className="mb-2 font-heading text-lg font-bold text-foreground-950">
                    没有匹配的追踪记录
                  </h2>
                  <p className="mb-5 max-w-md text-sm text-foreground-600">
                    换个关键词，或者清空分类与状态筛选再看看。也可能是这条线索还没被建档，你可以把线索投递给编辑部。
                  </p>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line" />
                    清空全部筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}